/**
 * WidgetShell
 *
 * Wrapper chrome rendered around every widget instance on the grid canvas.
 * Provides a title bar (with a grip drag handle + displayName + remove
 * button), an error boundary, and sizing constraints from the widget
 * contract. Only the grip starts a grid drag — the rest of the title bar is
 * inert so titles and toolbars can't be grabbed by accident.
 *
 * Registry-agnostic — accepts a WidgetRegistry prop so each host page can
 * resolve contracts from its own widget set.
 */

import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { GripVertical } from 'lucide-react';
import {
  WIDGET_DRAG_HANDLE_CLASS,
  type WidgetRegistry,
} from '../grid-dashboard';
import styles from './WidgetShell.module.css';

export { WIDGET_DRAG_HANDLE_CLASS };

export interface WidgetShellProps {
  instanceId: string;
  widgetType: string;
  registry: WidgetRegistry;
  onRemove: (instanceId: string) => void;
  children: ReactNode;
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class WidgetErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[WidgetShell] Widget render error:', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className={styles.errorFallback}>
          <span className={styles.errorIcon}>⚠</span>
          <span className={styles.errorMessage}>Something went wrong</span>
          {this.state.error?.message && (
            <span className={styles.errorDetail}>{this.state.error.message}</span>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

export const WidgetShell: React.FC<WidgetShellProps> = ({
  instanceId,
  widgetType,
  registry,
  onRemove,
  children,
}) => {
  const contract = registry.get(widgetType);
  const displayName = contract?.displayName ?? widgetType;
  const hideShellTitleBar = contract?.hideShellTitleBar === true;
  const hideShellRemoveButton = contract?.hideShellRemoveButton === true;
  const bare = hideShellTitleBar && hideShellRemoveButton;

  const handleRemove = () => {
    onRemove(instanceId);
  };

  const sizeStyle: React.CSSProperties = {};
  if (contract?.minSize) {
    sizeStyle.minWidth = 0;
    sizeStyle.minHeight = 0;
  }

  const shellClass = bare ? `${styles.shell} ${styles.shellBare}` : styles.shell;

  return (
    <div className={shellClass} style={sizeStyle}>
      {!hideShellTitleBar ? (
        <div className={styles.titleBar}>
          <div
            className={`${styles.dragHandle} ${WIDGET_DRAG_HANDLE_CLASS}`}
            role="button"
            aria-label={`Move ${displayName} widget`}
            title="Drag to move widget"
          >
            <GripVertical size={14} aria-hidden="true" />
          </div>
          <span className={styles.title}>{displayName}</span>
          <button
            className={styles.removeButton}
            onClick={handleRemove}
            aria-label={`Remove ${displayName} widget`}
            title="Remove widget"
          >
            ×
          </button>
        </div>
      ) : !hideShellRemoveButton ? (
        <button
          className={`${styles.removeButton} ${styles.floatingRemoveButton}`}
          onClick={handleRemove}
          aria-label={`Remove ${displayName} widget`}
          title="Remove widget"
        >
          ×
        </button>
      ) : null}

      <div className={styles.content}>
        <WidgetErrorBoundary>
          {children}
        </WidgetErrorBoundary>
      </div>
    </div>
  );
};

export default WidgetShell;
